import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { Link } from "react-router-dom";

// Her blog için benzersiz renk paleti üret (başlık hash'ine göre)
const colorPalettes = [
  { from: "from-violet-900", via: "via-purple-800", to: "to-fuchsia-700", accent: "bg-fuchsia-400", ring: "ring-fuchsia-400/20", text: "text-fuchsia-200" },
  { from: "from-slate-900", via: "via-cyan-900", to: "to-teal-700", accent: "bg-teal-400", ring: "ring-teal-400/20", text: "text-teal-200" },
  { from: "from-gray-900", via: "via-slate-800", to: "to-zinc-700", accent: "bg-slate-400", ring: "ring-slate-400/20", text: "text-slate-300" },
  { from: "from-rose-900", via: "via-pink-800", to: "to-red-700", accent: "bg-rose-400", ring: "ring-rose-400/20", text: "text-rose-200" },
  { from: "from-amber-900", via: "via-orange-800", to: "to-red-800", accent: "bg-amber-400", ring: "ring-amber-400/20", text: "text-amber-200" },
  { from: "from-indigo-900", via: "via-blue-800", to: "to-sky-700", accent: "bg-sky-400", ring: "ring-sky-400/20", text: "text-sky-200" },
  { from: "from-emerald-900", via: "via-green-800", to: "to-teal-700", accent: "bg-emerald-400", ring: "ring-emerald-400/20", text: "text-emerald-200" },
  { from: "from-stone-900", via: "via-neutral-800", to: "to-stone-700", accent: "bg-stone-400", ring: "ring-stone-400/20", text: "text-stone-300" },
];

function getColorForTitle(title, index) {
  return colorPalettes[index % colorPalettes.length];
}

// Canlı blog kapak kartı
function BlogCoverCard({ title, index }) {
  const colors = getColorForTitle(title, index);

  return (
    <div className={`relative w-full h-52 bg-gradient-to-br ${colors.from} ${colors.via} ${colors.to} overflow-hidden group`}>
      {/* Dekoratif daireler */}
      <div className={`absolute -top-8 -right-8 w-32 h-32 rounded-full ${colors.accent} opacity-10 group-hover:opacity-20 group-hover:scale-110 transition-all duration-700`} />
      <div className={`absolute top-16 -right-4 w-20 h-20 rounded-full ${colors.accent} opacity-[0.07] group-hover:opacity-15 group-hover:scale-125 transition-all duration-500 delay-100`} />
      <div className={`absolute -bottom-6 -left-6 w-24 h-24 rounded-full ${colors.accent} opacity-[0.08] group-hover:opacity-15 transition-all duration-600`} />

      {/* Sol accent çizgi */}
      <div className={`absolute left-0 top-6 bottom-6 w-1 ${colors.accent} opacity-40 rounded-r-full`} />

      {/* İnce çizgi deseni */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.1) 20px, rgba(255,255,255,0.1) 21px)`
      }} />

      {/* Başlık */}
      <div className="relative z-10 flex flex-col justify-center h-full px-6 pl-8">
        <span className={`text-xs font-medium uppercase tracking-[0.2em] ${colors.text} opacity-70 mb-2`}>
          Blog
        </span>
        <h3 className="text-white text-lg font-bold leading-snug line-clamp-3 drop-shadow-sm">
          {title}
        </h3>
      </div>

      {/* Alt gradient overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/20 to-transparent" />
    </div>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const q = query(
          collection(db, "posts"),
          orderBy("createdAt", "desc")
        );

        const snap = await getDocs(q);

        setPosts(
          snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );
      } catch (error) {
        console.log("Bloglar alınamadı:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );

  return (
    <div className="min-h-screen py-16 px-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-12 text-pink-600">
          Blog Yazıları
        </h1>

        {posts.length === 0 && (
          <div className="text-center text-gray-500">
            Henüz blog yazısı yok.
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.id}`}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col"
            >
              {/* Görsel: imageUrl varsa göster, yoksa canlı CSS kart */}
              {post.imageUrl ? (
                <img
                  src={post.imageUrl}
                  alt={post.title}
                  className="w-full h-52 object-cover"
                />
              ) : (
                <BlogCoverCard title={post.title} index={posts.indexOf(post)} />
              )}

              <div className="p-6 flex-1">
                <h2 className="text-xl font-semibold mb-2 line-clamp-2">
                  {post.title}
                </h2>

                <p className="text-sm text-gray-500 mb-3">
                  {post.createdAt?.toDate
                    ? post.createdAt
                        .toDate()
                        .toLocaleDateString("tr-TR")
                    : ""}
                </p>

                <p className="text-gray-600 text-sm line-clamp-3">
                  {post.content}
                </p>

                <div className="mt-4 text-pink-600 font-medium">
                  Devamını Oku →
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}